/**
 * Plain words for a failed call on the App Experience page (ruling R17: every
 * refusal says what is wrong and how to fix it; no raw server text, no URL, no
 * developer word reaches the admin).
 *
 * `adminFetch` throws an AdminApiError with the HTTP status and the parsed
 * body (RFC 9457: `code`, `detail`); API Gateway answers `{ message }`; a
 * network fault is a TypeError ("Failed to fetch"). This file reads those
 * facts without importing the provider (it is pure, so the root jest tests it
 * directly) and turns them into words. `voiceErrorText`
 * (./voice-generation.ts) uses the same facts for the voice routes.
 */

export interface ErrorFacts {
  /** HTTP status; null when the request never got an answer. */
  status: number | null;
  /** Our API's stable machine code (`app_experience_*`), when there is one. */
  code: string | null;
  /** Our API's `detail` text. Read for facts only; never shown as is. */
  detail: string | null;
  message: string;
  /** The request did not reach the server (fetch threw a TypeError). */
  network: boolean;
}

export function errorFacts(e: unknown): ErrorFacts {
  const o = e && typeof e === 'object' ? (e as { status?: unknown; body?: unknown; message?: unknown }) : {};
  const body = o.body && typeof o.body === 'object' ? (o.body as { code?: unknown; detail?: unknown }) : {};
  return {
    status: typeof o.status === 'number' ? o.status : null,
    code: typeof body.code === 'string' ? body.code : null,
    detail: typeof body.detail === 'string' ? body.detail : null,
    message: typeof o.message === 'string' ? o.message : '',
    network: e instanceof TypeError,
  };
}

/** The gateway or the server gave up (API Gateway answers 504 "Endpoint request timed out"). */
export function isTimeout(f: ErrorFacts): boolean {
  return f.status === 502 || f.status === 503 || f.status === 504 || /timed out/i.test(f.message);
}

export const TOO_MANY_TEXT = 'Too many requests. Wait one minute, then try again.';

const MISSING_VOICE_TEXT = "Abby's voice file is missing on the media server. Generate Abby's voice again, then publish.";
const MISSING_VIDEO_TEXT = 'A video file is missing on the media server. Upload the video again, then publish.';

/**
 * Which kinds of file a "missing on the media host" refusal names. The URLs
 * are the PUBLIC ones (`<origin>/welcome/<hash>/<locale>.mp3`,
 * `<origin>/welcome-tts/<hash>.mp3` = Abby's voice; `<origin>/intro/<sha>.mp4|.jpg`
 * = a video or its poster): the CDN serves the bucket's m/ prefix at its root,
 * so a real URL has no `/m/`. A bucket key (`m/welcome/...`) matches too. The
 * URLs are read here and never shown.
 */
const VOICE_PATH = /\/welcome(-tts)?\//;

function missingKinds(detail: string): { voice: boolean; video: boolean } {
  const urls = detail.match(/https?:\/\/\S+/g) ?? [];
  const voice = urls.some((u) => VOICE_PATH.test(u));
  const video = urls.some((u) => !VOICE_PATH.test(u));
  return { voice, video };
}

const MISMATCHED_VOICE_TEXT =
  "Abby's voice in this version does not match the file on the media server. Generate Abby's voice again, then publish.";
const MISMATCHED_VIDEO_TEXT =
  'A video in this version does not match the converted file on the media server. Upload the video again, then publish.';

/** The words for a failed publish (both tabs). A 409 is handled before this, as the conflict. */
export function publishErrorText(e: unknown): string {
  const f = errorFacts(e);
  if (f.network) return 'The connection failed. Check the internet connection, then publish again.';
  if (f.status === 401) return 'Your session ended. Sign in again, then publish.';
  if (f.status === 403) return 'You do not have permission to publish. Ask a Super Admin for the experience.write permission.';
  if (f.status === 429) return TOO_MANY_TEXT;
  if (f.status === 400 && f.code === 'app_experience_note') {
    return 'The note has a character that cannot be saved (a control, invisible or broken character). Remove it, then publish again.';
  }
  if (f.status === 400 && f.code === 'app_experience_voice_mismatch') return MISMATCHED_VOICE_TEXT;
  if (f.status === 400 && f.code === 'app_experience_video_mismatch') return MISMATCHED_VIDEO_TEXT;
  if (f.status === 400 && f.code === 'app_experience_media_missing') {
    const kinds = missingKinds(f.detail ?? '');
    const words = [...(kinds.voice ? [MISSING_VOICE_TEXT] : []), ...(kinds.video ? [MISSING_VIDEO_TEXT] : [])];
    return words.length > 0
      ? words.join(' ')
      : "A file of this version is missing on the media server. Generate Abby's voice or upload the video again, then publish.";
  }
  if (isTimeout(f)) {
    return 'The server did not answer in time. The version may be saved. Reload the page to check, then publish again if you need to.';
  }
  if (f.status !== null && f.status >= 500) return `The server had a problem (error ${f.status}). Wait one minute, then publish again.`;
  if (f.status !== null && f.status >= 400) return 'The server refused this version. Reload the page, check your changes, then publish again.';
  return 'The change was not saved. Try again.';
}

// ─── upload and page load (final whole-branch review minor 1) ───────────────

/**
 * An Error whose message THIS page wrote in plain words (the storage upload
 * words of upload-flow.ts uploadErrorText). Shown as is; every other error
 * goes through the facts below.
 */
export class PlainWordsError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'PlainWordsError';
  }
}

/**
 * Codes whose `detail` the backend writes for the admin, in plain words with no
 * URL and no developer word (backend/src/routes/admin-app-experience.ts and
 * app-experience/uploads.ts). Any other code (for example the schema refusal
 * `app_experience_bad_request`, which carries raw validator text) is not shown.
 */
const UPLOAD_CODES_WITH_WORDS: ReadonlySet<string> = new Set([
  'app_experience_file_type',
  'app_experience_file_too_big',
  'app_experience_file_name',
  'app_experience_upload_incomplete',
  'app_experience_upload_size_mismatch',
  'app_experience_upload_not_waiting',
  'app_experience_upload_not_found',
  'app_experience_bad_upload_id',
]);

/** The words for a failed upload step (create, transfer, start, status). */
export function uploadErrorWords(e: unknown): string {
  if (e instanceof PlainWordsError) return e.message;
  const f = errorFacts(e);
  if (f.network) return 'The connection failed. Check the internet connection, then upload the file again.';
  if (f.status === 401) return 'Your session ended. Sign in again, then upload the file again.';
  if (f.status === 403) return 'You do not have permission to upload. Ask a Super Admin for the experience.write permission.';
  if (f.status === 429) return TOO_MANY_TEXT;
  if (f.code !== null && UPLOAD_CODES_WITH_WORDS.has(f.code) && f.detail) return f.detail;
  if (isTimeout(f)) return 'The server did not answer in time. Wait one minute, then upload the file again.';
  if (f.status !== null && f.status >= 500) return `The server had a problem (error ${f.status}). Wait one minute, then upload the file again.`;
  if (f.status !== null && f.status >= 400) return 'The server refused this file. Check the file name and the size, then upload the file again.';
  return 'The upload did not finish. Upload the file again.';
}

/** The words for a page load that failed (shown under "The page could not load." or "… reload."). */
export function loadErrorText(e: unknown): string {
  const f = errorFacts(e);
  if (f.network) return 'The connection failed. Check the internet connection, then try again.';
  if (f.status === 401) return 'Your session ended. Sign in again, then reload the page.';
  if (f.status === 403) return 'You do not have permission to view this page. Ask a Super Admin for the experience.read permission.';
  if (f.status === 429) return TOO_MANY_TEXT;
  if (isTimeout(f)) return 'The server did not answer in time. Wait one minute, then try again.';
  if (f.status !== null && f.status >= 500) return `The server had a problem (error ${f.status}). Wait one minute, then try again.`;
  return 'Try again. If this repeats, tell engineering.';
}
